/**
 * /ny-hemsida — Google Ads landning
 * ---------------------------------
 * En liten, isolerad tracking-modul för /ny-hemsida.
 *
 * Konventioner (från prompten):
 *   • Data pushas till window.dataLayer via de fem eventnamnen nedan.
 *   • `ny_hemsida_form_success` skickas ENDAST efter verifierat lyckat
 *     backend-svar och ENDAST en gång per sidladdning.
 *   • Inga nya Google Ads conversion labels hårdkodas här. Om Elido i
 *     Google Ads UI kopplar en conversion action mot ett av eventen
 *     nedan sköts det via befintlig gtag/GTM, inte via denna fil.
 *   • Attribution (gclid/gbraid/wbraid/utm_*) läses från URL:en och
 *     persisteras i sessionStorage så att första-touch bevaras även
 *     om användaren scrollar/nav:erar innan formuläret skickas.
 *   • Ingen persistent cookie sätts här — Consent Mode v2 styr all
 *     cookie-baserad spårning globalt (utils/cookieConsent.js).
 */

const FORM_SUCCESS_SENT_KEY = "ny_hemsida_form_success_sent";

// Attribution-capturen är GENERALISERAD till utils/attribution.js och
// delas av alla lead-formulär. Vi re-exporterar den här så att befintliga
// /ny-hemsida-importer fortsätter fungera oförändrat (ingen parallell variant).
import { captureAttribution, getAttribution } from "@/utils/attribution";
import { getConsent } from "@/utils/cookieConsent";

export { captureAttribution, getAttribution };

export const NY_HEMSIDA_EVENTS = Object.freeze({
  CTA_CLICK: "ny_hemsida_cta_click",
  FORM_START: "ny_hemsida_form_start",
  FORM_SUCCESS: "ny_hemsida_form_success",
  CASE_CLICK: "ny_hemsida_case_click",
  GOOGLE_REVIEWS_CLICK: "ny_hemsida_google_reviews_click",
});

/**
 * Push till dataLayer. Skapar arrayen om den saknas.
 * Skickar aldrig `undefined`-värden.
 */
function dataLayerPush(payload) {
  if (typeof window === "undefined") return;
  // dataLayer/GA4-events är analytics → kräver statistics-samtycke.
  // Köa inget i dataLayer utan samtycke (Basic Consent Mode).
  try {
    if (!getConsent().statistics) return;
  } catch {
    return;
  }
  try {
    window.dataLayer = window.dataLayer || [];
    const clean = {};
    for (const [k, v] of Object.entries(payload)) {
      if (v !== undefined && v !== null && v !== "") clean[k] = v;
    }
    window.dataLayer.push(clean);
  } catch {
    /* aldrig krascha på tracking */
  }
}

/** CTA-klick (hero, hur-det-fungerar, prislåda osv). */
export function trackCtaClick(location) {
  dataLayerPush({
    event: NY_HEMSIDA_EVENTS.CTA_CLICK,
    ny_hemsida_location: location || "unknown",
  });
}

/** Fältfokus/första interaktion i formuläret — skickas max en gång. */
let _formStartSent = false;
export function trackFormStart() {
  if (_formStartSent) return;
  _formStartSent = true;
  dataLayerPush({ event: NY_HEMSIDA_EVENTS.FORM_START });
}

/**
 * Skickas endast efter verifierat lyckat backend-svar och max en gång
 * per sidladdning (skyddar mot dubbla submits, React-strict-mode osv).
 *
 * Utöver befintlig dataLayer-push skickas eventet exakt en gång per
 * lead_id till GA4 via befintlig gtag-installation. Endast lead_id
 * skickas — ingen PII (e-post, telefon, företagsnamn). Anroparen
 * ansvarar för att bara kalla vid `accepted === true`.
 */
const _sentLeadIds = new Set();
export function trackFormSuccess(extra = {}) {
  if (typeof window === "undefined") return;
  if (window[FORM_SUCCESS_SENT_KEY]) return;
  const leadId = extra && extra.ny_hemsida_lead_id;
  if (leadId && _sentLeadIds.has(leadId)) return;
  window[FORM_SUCCESS_SENT_KEY] = true;
  if (leadId) _sentLeadIds.add(leadId);

  // 1) Befintlig dataLayer-push (oförändrad).
  dataLayerPush({ event: NY_HEMSIDA_EVENTS.FORM_SUCCESS, ...extra });

  // 2) GA4 via befintlig gtag-installation. Ingen ny loader/property.
  //    Skickar endast lead_id — ingen PII. Analytics → statistics-samtycke.
  try {
    if (getConsent().statistics && leadId && typeof window.gtag === "function") {
      window.gtag("event", NY_HEMSIDA_EVENTS.FORM_SUCCESS, {
        lead_id: leadId,
      });
    }
  } catch {
    /* aldrig krascha på tracking */
  }

  // 3) Meta Pixel Lead — DELAR exakt samma success-path som GA4 ovan, så
  //    Meta och GA4 mäter samma faktiska händelse (bekräftad lyckad
  //    submission, en gång per lead). Triggas ALDRIG på knappklick eller
  //    form_start. Skickas ENDAST med marknadsföringssamtycke (window.fbq
  //    finns bara efter consent, se utils/metaPixel.js) och kraschar aldrig
  //    sidan om Pixeln inte hunnit laddas.
  //    eventID = lead_id så Pixel + backend CAPI-Lead dedupliceras
  //    server-side av Meta (samma event_name + eventID inom 24h).
  try {
    if (getConsent().marketing && typeof window.fbq === "function") {
      if (leadId) {
        window.fbq("track", "Lead", {}, { eventID: leadId });
      } else {
        window.fbq("track", "Lead");
      }
    }
  } catch {
    /* aldrig krascha på tracking */
  }
}

/** Klick på ett kundcase (till extern webbplats). */
export function trackCaseClick(caseId) {
  dataLayerPush({
    event: NY_HEMSIDA_EVENTS.CASE_CLICK,
    ny_hemsida_case: caseId || "unknown",
  });
}

/** Klick på Google-recensionsraden. */
export function trackGoogleReviewsClick(location) {
  dataLayerPush({
    event: NY_HEMSIDA_EVENTS.GOOGLE_REVIEWS_CLICK,
    ny_hemsida_location: location || "unknown",
  });
}

/** Testhjälp — nollställ interna one-shot flaggor (används av tester). */
export function _resetForTests() {
  _formStartSent = false;
  _sentLeadIds.clear();
  if (typeof window !== "undefined") delete window[FORM_SUCCESS_SENT_KEY];
}
